import i18n from 'i18next';

import {SMART_SEARCH_KEY} from '../constants/smartSearchKey';
import type {EagFilterDefinition} from '../types/EagColumn';
import type {EagColumnOrGroup} from '../types/EagColumnGroup';
import type {Filters} from '../types/Filters';
import {flattenColumnConfig} from './flattenColumnConfig';
import {isFilterValueActive} from './isFilterValueActive';

const NUMERIC_RANGE_FILTER_TYPES = new Set(['RANGE', 'SLIDER_RANGE', 'FILE_SIZE_SLIDER']);

/**
 * Provenance tag rendered as the small uppercase badge on each chip (mock
 * .fchip__src). EagGrid keeps all filters in one unified `filterValues` map with
 * no origin marker, so the only honest distinction available is column filter vs
 * the free-text smart search. `search` chips render no badge (their label already
 * reads "Search"); `column` chips get the badge.
 */
export type ActiveFilterChipSource = 'column' | 'search';

export type ActiveFilterChip = {
  key: string;
  label: string;
  valueLabel: string;
  source: ActiveFilterChipSource;
};

export type BuildActiveFilterChipsOptions = {
  searchLabel?: string;
  excludeKeys?: Set<string>;
  /** Externally resolved value→label options per column key — lazily loaded choice
   *  options and async suggestion labels that don't live in the column definition. */
  extraOptionsByColumnKey?: Map<string, Array<{key: string; label: string}>>;
};

/**
 * All value→label pairs a column's filter definition knows about: static options
 * plus the quick-filter / relative-date sentinels (whose BE-translated labels the
 * filter widgets render as chips). An applied sentinel is stored as its raw value
 * (e.g. `QUICK_FILTER_FILLED`, `TODAY`), so the chip must map it back to the label.
 * Extra (externally resolved) options seed the map first so definition labels win
 * on key collision.
 */
const buildLabelByValue = (
  filter: EagFilterDefinition | undefined,
  extraOptions: Array<{key: string; label: string}> | undefined
): Map<string, string> => {
  const labelByValue = new Map<string, string>();
  extraOptions?.forEach((o) => labelByValue.set(o.key, o.label));
  filter?.quickFilters?.forEach((o) => labelByValue.set(o.value, o.label));
  filter?.relativeDates?.forEach((o) => labelByValue.set(o.value, o.label));
  filter?.options?.forEach((o) => labelByValue.set(o.key, o.label));
  return labelByValue;
};

const isRelativeInputValue = (
  value: object
): value is {relativeInputValue: number; relativeInputKey: string} =>
  'relativeInputKey' in value && 'relativeInputValue' in value;

const formatValue = (
  raw: unknown,
  filter: EagFilterDefinition | undefined,
  extraOptions: Array<{key: string; label: string}> | undefined
): string => {
  // Unwrap the {value: X} wrapper form used by EagGrid filter types
  const value =
    typeof raw === 'object' && raw !== null && 'value' in (raw as Record<string, unknown>)
      ? (raw as Record<string, unknown>).value
      : raw;

  const labelByValue = buildLabelByValue(filter, extraOptions);

  const resolveLabel = (v: unknown): string => {
    if (typeof v === 'boolean') {
      return v ? i18n.t('general.labels.yes') : i18n.t('general.labels.no');
    }
    if (typeof v === 'string') {
      return labelByValue.get(v) ?? v;
    }
    return String(v);
  };

  if (Array.isArray(value)) {
    return value.map(resolveLabel).join(', ');
  }
  if (typeof value === 'object' && value !== null) {
    // Relative date input model, e.g. {relativeInputValue: 5, relativeInputKey: 'LAST_N_DAYS'}
    if (isRelativeInputValue(value)) {
      const inputLabel =
        filter?.relativeInputs?.find((ri) => ri.value === value.relativeInputKey)?.label ??
        value.relativeInputKey;
      return `${inputLabel}: ${value.relativeInputValue}`;
    }
    if ('from' in value || 'to' in value) {
      const {from, to} = value as {from?: unknown; to?: unknown};
      // One-sided intervals need a bound label; date-specific labels are only
      // appropriate for date filters, not numeric ranges loaded from AG Grid.
      if (from != null && to != null) {
        return `${from} – ${to}`;
      }
      const isNumericRange = NUMERIC_RANGE_FILTER_TYPES.has(filter?.type ?? '');
      if (from != null) {
        const label = i18n.t(
          isNumericRange ? 'general.labels.from' : 'page.datagrid.filter.dateLabelFrom'
        );
        return `${label} ${from}`;
      }
      const label = i18n.t(
        isNumericRange ? 'general.labels.to' : 'page.datagrid.filter.dateLabelTo'
      );
      return `${label} ${to}`;
    }
  }
  return resolveLabel(value);
};

/** The free-text smart-search chip (no source badge; its label already reads "Search"). */
const buildSearchChip = (key: string, state: unknown, searchLabel: string): ActiveFilterChip => ({
  key,
  label: searchLabel,
  valueLabel: String((state as Record<string, unknown>)?.[SMART_SEARCH_KEY] ?? ''),
  source: 'search',
});

/**
 * Maps the store's unified `filterValues` to a flat, ordered list of removable
 * chips for the active-filter bar. Pure — no store/React access (i18n is read via
 * the module-level i18next instance, like toPlainLabel). The free-text search
 * lives under SMART_SEARCH_KEY and is surfaced as a chip too.
 */
export const buildActiveFilterChips = (
  filterValues: Filters,
  columnConfig: EagColumnOrGroup[],
  options?: BuildActiveFilterChipsOptions
): ActiveFilterChip[] => {
  const searchLabel = options?.searchLabel ?? 'Search';
  const flatColumns = flattenColumnConfig(columnConfig);
  const columnByKey = new Map(flatColumns.map((c) => [c.key, c]));
  return Object.entries(filterValues)
    .filter(([key, state]) => !options?.excludeKeys?.has(key) && isFilterValueActive(state))
    .map(([key, state]) => {
      if (key === SMART_SEARCH_KEY) {
        return buildSearchChip(key, state, searchLabel);
      }
      const column = columnByKey.get(key);
      return {
        key,
        label: column?.headerName ?? key,
        valueLabel: formatValue(state, column?.filter, options?.extraOptionsByColumnKey?.get(key)),
        source: 'column' as const,
      };
    });
};
